/**
 * This module contains custom shaders related code.
 * @module shaders
 */
import { Vector2, Matrix4, UniformsUtils, UniformsLib, Color, Vector3, ShaderChunk } from "three";


/**
 * This class holds the available shaders.
 * See https://threejs.org/docs/#api/en/materials/ShaderMaterial.
 */
export class Shaders {

  /** The mirror shader (used by water). */
  public static MirrorShader: any = {

    uniforms: UniformsUtils.merge([
      UniformsLib.fog,
      UniformsLib.lights,
      {
        normalSampler: { value: null },
        mirrorSampler: { value: null },
        alpha: { value: 0.9 },
        time: { value: 0.0 },
        size: { value: 1.0 },
        distortionScale: { value: 20.0 },
        textureMatrix: { value: new Matrix4() },
        sunColor: { value: new Color(0x7F7F7F) },
        sunDirection: { value: new Vector3(0.70707, 0.70707, 0) },
        eye: { value: new Vector3() },
        waterColor: { value: new Color(0x555555) },
        speed: 0,
        rotX: 1,
        rotY: 1
      }
    ]),

    vertexShader: [
      "uniform mat4 textureMatrix;",
      "uniform float time;",

      "varying vec4 mirrorCoord;",
      "varying vec4 worldPosition;",

      ShaderChunk.fog_pars_vertex,
      ShaderChunk.shadowmap_pars_vertex,
      ShaderChunk.morphtarget_pars_vertex,

      "void main() {",
      ShaderChunk.begin_vertex,
      ShaderChunk.morphtarget_vertex,

      "	mirrorCoord = modelMatrix * vec4( position, 1.0 );",
      "	worldPosition = mirrorCoord.xyzw;",
      "	mirrorCoord = textureMatrix * mirrorCoord;",
      "	vec4 mvPosition =  modelViewMatrix * vec4( transformed, 1.0 );",
      "	gl_Position = projectionMatrix * mvPosition;",

      ShaderChunk.fog_vertex,
      ShaderChunk.shadowmap_vertex,

      "}"
    ].join("\n"),

    fragmentShader: [
      "uniform sampler2D mirrorSampler;",
      "uniform float alpha;",
      "uniform float time;",
      "uniform float size;",
      "uniform float distortionScale;",
      "uniform sampler2D normalSampler;",
      "uniform vec3 sunColor;",
      "uniform vec3 sunDirection;",
      "uniform vec3 eye;",
      "uniform vec3 waterColor;",
      "uniform float speed;",
      "uniform float rotX;",
      "uniform float rotY;",

      "varying vec4 mirrorCoord;",
      "varying vec4 worldPosition;",

      "vec4 getNoise( vec2 uv ) {",
      "	vec2 uv0 = ( uv / 103.0 ) + vec2(time / (17.0 * rotX) * speed, time / (29.0 * rotY) * speed);",
      "	vec2 uv1 = ( uv / 107.0 ) - vec2( time / (-19.0 * rotX) * speed, time / (31.0 * rotY) * speed );",
      "	vec2 uv2 = ( uv.xy / vec2( 8907.0, 9803.0 ) ) + vec2( time / (101.0 * rotX) * speed, time / (97.0 * rotY) * speed );",
      "	vec2 uv3 = ( uv.xy / vec2( 1091.0, 1027.0 ) ) - vec2( time / (109.0 * rotX) * speed, time / (-113.0 * rotY) * speed );",
      "	vec4 noise = texture2D( normalSampler, uv0 ) +",
      "		texture2D( normalSampler, uv1 ) +",
      "		texture2D( normalSampler, uv2 ) +",
      "		texture2D( normalSampler, uv3 );",
      "	return noise * 0.5 - 1.0;",
      "}",

      "void sunLight( const vec3 surfaceNormal, const vec3 eyeDirection, float shiny, float spec, float diffuse, inout vec3 diffuseColor, inout vec3 specularColor ) {",
      "	vec3 reflection = normalize( reflect( -sunDirection, surfaceNormal ) );",
      "	float direction = max( 0.0, dot( eyeDirection, reflection ) );",
      "	specularColor += pow( direction, shiny ) * sunColor * spec;",
      "	diffuseColor += max( dot( sunDirection, surfaceNormal ), 0.0 ) * sunColor * diffuse;",
      "}",

      ShaderChunk.common,
      ShaderChunk.packing,
      ShaderChunk.bsdfs,
      ShaderChunk.fog_pars_fragment,
      ShaderChunk.lights_pars_begin,
      ShaderChunk.shadowmap_pars_fragment,
      ShaderChunk.shadowmask_pars_fragment,

      "void main() {",
      "	vec4 noise = getNoise((worldPosition.xz ) * size);",
      "	vec3 surfaceNormal = normalize( noise.xzy * vec3( 1.5, 1.0, 1.5 ) );",

      "	vec3 diffuseLight = vec3(0.0);",
      "	vec3 specularLight = vec3(0.0);",

      "	vec3 worldToEye = eye-worldPosition.xyz;",
      "	vec3 eyeDirection = normalize( worldToEye );",
      "	sunLight( surfaceNormal, eyeDirection, 100.0, 2.0, 0.5, diffuseLight, specularLight );",

      "	float distance = length(worldToEye);",

      "	vec2 distortion = surfaceNormal.xz * ( 0.001 + 1.0 / distance ) * distortionScale;",
      "	vec3 reflectionSample = vec3( texture2D( mirrorSampler, mirrorCoord.xy / mirrorCoord.w + distortion ) );",

      "	float theta = max( dot( eyeDirection, surfaceNormal ), 0.0 );",
      "	float rf0 = 0.3;",
      "	float reflectance = rf0 + ( 1.0 - rf0 ) * pow( ( 1.0 - theta ), 5.0 );",
      "	vec3 scatter = max( 0.0, dot( surfaceNormal, eyeDirection ) ) * waterColor;",
      "	vec3 albedo = mix( ( sunColor * diffuseLight * 0.3 + scatter ) * getShadowMask(), ( vec3( 0.1 ) + reflectionSample * 0.9 + reflectionSample * specularLight ), reflectance);",
      "	vec3 outgoingLight = albedo;",
      "	gl_FragColor = vec4( outgoingLight, alpha );",

      ShaderChunk.tonemapping_fragment,
      ShaderChunk.fog_fragment,

      "}"
    ].join("\n")

  };

  /** The copy shader. */
  public static CopyShader: any = {

    uniforms: {

      tDiffuse: { value: null },
      opacity: { value: 1.0 }

    },

    vertexShader: [

      "varying vec2 vUv;",

      "void main() {",

      "vUv = uv;",
      "gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );",

      "}"

    ].join("\n"),

    fragmentShader: [

      "uniform float opacity;",

      "uniform sampler2D tDiffuse;",

      "varying vec2 vUv;",

      "void main() {",

      "vec4 texel = texture2D( tDiffuse, vUv );",
      "gl_FragColor = opacity * texel;",

      "}"

    ].join("\n")

  };

  /** The Screen Space Ambient Occlusion shader. */
  public static SSAOShader: any = {

    defines: {
      PERSPECTIVE_CAMERA: 1,
      KERNEL_SIZE: 32
    },

    uniforms: {

      tDiffuse: { value: null },
      tNormal: { value: null },
      tDepth: { value: null },
      tNoise: { value: null },
      kernel: { value: null },
      cameraNear: { value: null },
      cameraFar: { value: null },
      resolution: { value: new Vector2() },
      cameraProjectionMatrix: { value: new Matrix4() },
      cameraInverseProjectionMatrix: { value: new Matrix4() },
      kernelRadius: { value: 8 },
      minDistance: { value: 0.005 },
      maxDistance: { value: 0.05 }

    },

    vertexShader: [

      "varying vec2 vUv;",

      "void main() {",

      "	vUv = uv;",

      "	gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );",

      "}"

    ].join("\n"),

    fragmentShader: [

      "uniform sampler2D tDiffuse;",
      "uniform sampler2D tNormal;",
      "uniform sampler2D tDepth;",
      "uniform sampler2D tNoise;",

      "uniform vec3 kernel[ KERNEL_SIZE ];",

      "uniform vec2 resolution;",

      "uniform float cameraNear;",
      "uniform float cameraFar;",
      "uniform mat4 cameraProjectionMatrix;",
      "uniform mat4 cameraInverseProjectionMatrix;",

      "uniform float kernelRadius;",
      "uniform float minDistance;", // avoid artifacts caused by neighbour fragments with minimal depth difference
      "uniform float maxDistance;", // avoid the influence of fragments which are too far away

      "varying vec2 vUv;",

      "#include <packing>",

      "float getDepth( const in vec2 screenPosition ) {",

      "	return texture2D( tDepth, screenPosition ).x;",

      "}",

      "float getLinearDepth( const in vec2 screenPosition ) {",

      "	#if PERSPECTIVE_CAMERA == 1",

      "		float fragCoordZ = texture2D( tDepth, screenPosition ).x;",
      "		float viewZ = perspectiveDepthToViewZ( fragCoordZ, cameraNear, cameraFar );",
      "		return viewZToOrthographicDepth( viewZ, cameraNear, cameraFar );",

      "	#else",

      "		return texture2D( depthSampler, coord ).x;",

      "	#endif",

      "}",

      "float getViewZ( const in float depth ) {",

      "	#if PERSPECTIVE_CAMERA == 1",

      "		return perspectiveDepthToViewZ( depth, cameraNear, cameraFar );",

      "	#else",

      "		return orthographicDepthToViewZ( depth, cameraNear, cameraFar );",

      "	#endif",

      "}",

      "vec3 getViewPosition( const in vec2 screenPosition, const in float depth, const in float viewZ ) {",

      "	float clipW = cameraProjectionMatrix[2][3] * viewZ + cameraProjectionMatrix[3][3];",

      "	vec4 clipPosition = vec4( ( vec3( screenPosition, depth ) - 0.5 ) * 2.0, 1.0 );",

      "	clipPosition *= clipW; // unprojection.",

      "	return ( cameraInverseProjectionMatrix * clipPosition ).xyz;",

      "}",

      "vec3 getViewNormal( const in vec2 screenPosition ) {",

      "	return unpackRGBToNormal( texture2D( tNormal, screenPosition ).xyz );",

      "}",

      "void main() {",

      "	float depth = getDepth( vUv );",
      "	float viewZ = getViewZ( depth );",

      "	vec3 viewPosition = getViewPosition( vUv, depth, viewZ );",
      "	vec3 viewNormal = getViewNormal( vUv );",

      " vec2 noiseScale = vec2( resolution.x / 4.0, resolution.y / 4.0 );",
      "	vec3 random = texture2D( tNoise, vUv * noiseScale ).xyz;",

      // compute matrix used to reorient a kernel vector

      "	vec3 tangent = normalize( random - viewNormal * dot( random, viewNormal ) );",
      "	vec3 bitangent = cross( viewNormal, tangent );",
      "	mat3 kernelMatrix = mat3( tangent, bitangent, viewNormal );",

      " float occlusion = 0.0;",

      " for ( int i = 0; i < KERNEL_SIZE; i ++ ) {",

      "		vec3 sampleVector = kernelMatrix * kernel[ i ];", // reorient sample vector in view space
      "		vec3 samplePoint = viewPosition + ( sampleVector * kernelRadius );", // calculate sample point

      "		vec4 samplePointNDC = cameraProjectionMatrix * vec4( samplePoint, 1.0 );", // project point and calculate NDC
      "		samplePointNDC /= samplePointNDC.w;",

      "		vec2 samplePointUv = samplePointNDC.xy * 0.5 + 0.5;", // compute uv coordinates

      "		float realDepth = getLinearDepth( samplePointUv );", // get linear depth from depth texture
      "		float sampleDepth = viewZToOrthographicDepth( samplePoint.z, cameraNear, cameraFar );", // compute linear depth of the sample view Z value
      "		float delta = sampleDepth - realDepth;",

      "		if ( delta > minDistance && delta < maxDistance ) {", // if fragment is before sample point, increase occlusion

      "			occlusion += 1.0;",

      "		}",

      "	}",

      "	occlusion = clamp( occlusion / float( KERNEL_SIZE ), 0.0, 1.0 );",

      "	gl_FragColor = vec4( vec3( 1.0 - occlusion ), 1.0 );",

      "}"

    ].join("\n")

  };

  /** The Screen Space Ambient Occlusion Depth shader. */
  public static SSAODepthShader: any = {

    defines: {
      PERSPECTIVE_CAMERA: 1
    },

    uniforms: {

      tDepth: { value: null },
      cameraNear: { value: null },
      cameraFar: { value: null }

    },

    vertexShader: [

      "varying vec2 vUv;",

      "void main() {",

      "	vUv = uv;",
      "	gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );",

      "}"

    ].join("\n"),

    fragmentShader: [

      "uniform sampler2D tDepth;",

      "uniform float cameraNear;",
      "uniform float cameraFar;",

      "varying vec2 vUv;",

      "#include <packing>",

      "float getLinearDepth( const in vec2 screenPosition ) {",

      "	#if PERSPECTIVE_CAMERA == 1",

      "		float fragCoordZ = texture2D( tDepth, screenPosition ).x;",
      "		float viewZ = perspectiveDepthToViewZ( fragCoordZ, cameraNear, cameraFar );",
      "		return viewZToOrthographicDepth( viewZ, cameraNear, cameraFar );",

      "	#else",

      "		return texture2D( depthSampler, coord ).x;",

      "	#endif",

      "}",

      "void main() {",

      "	float depth = getLinearDepth( vUv );",
      "	gl_FragColor = vec4( vec3( 1.0 - depth ), 1.0 );",

      "}"

    ].join("\n")

  };

  /** The Screen Space Ambient Occlusion Blur shader. */
  public static SSAOBlurShader: any = {

    uniforms: {

      tDiffuse: { value: null },
      resolution: { value: new Vector2() }

    },

    vertexShader: [

      "varying vec2 vUv;",

      "void main() {",

      "	vUv = uv;",
      "	gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );",

      "}"

    ].join("\n"),

    fragmentShader: [

      "uniform sampler2D tDiffuse;",

      "uniform vec2 resolution;",

      "varying vec2 vUv;",

      "void main() {",

      "	vec2 texelSize = ( 1.0 / resolution );",
      "	float result = 0.0;",

      "	for ( int i = - 2; i <= 2; i ++ ) {",

      "		for ( int j = - 2; j <= 2; j ++ ) {",

      "			vec2 offset = ( vec2( float( i ), float( j ) ) ) * texelSize;",
      "			result += texture2D( tDiffuse, vUv + offset ).r;",

      "		}",

      "	}",

      "	gl_FragColor = vec4( vec3( result / ( 5.0 * 5.0 ) ), 1.0 );",

      "}"

    ].join("\n")

  };

  /** The Enhanced Subpixel Morphological Antialiasing Edges shader. */
  public static SMAAEdgesShader: any = {

    defines: {

      SMAA_THRESHOLD: "0.1"

    },

    uniforms: {

      tDiffuse: { value: null },
      resolution: { value: new Vector2(1 / 1024, 1 / 512) }

    },

    vertexShader: [

      "uniform vec2 resolution;",

      "varying vec2 vUv;",
      "varying vec4 vOffset[ 3 ];",

      "void SMAAEdgeDetectionVS( vec2 texcoord ) {",
      "vOffset[ 0 ] = texcoord.xyxy + resolution.xyxy * vec4( -1.0, 0.0, 0.0,  1.0 );", // WebGL port note: Changed sign in W component
      "vOffset[ 1 ] = texcoord.xyxy + resolution.xyxy * vec4(  1.0, 0.0, 0.0, -1.0 );", // WebGL port note: Changed sign in W component
      "vOffset[ 2 ] = texcoord.xyxy + resolution.xyxy * vec4( -2.0, 0.0, 0.0,  2.0 );", // WebGL port note: Changed sign in W component
      "}",

      "void main() {",

      "vUv = uv;",

      "SMAAEdgeDetectionVS( vUv );",

      "gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );",

      "}"

    ].join("\n"),

    fragmentShader: [

      "uniform sampler2D tDiffuse;",

      "varying vec2 vUv;",
      "varying vec4 vOffset[ 3 ];",

      "vec4 SMAAColorEdgeDetectionPS( vec2 texcoord, vec4 offset[3], sampler2D colorTex ) {",
      "vec2 threshold = vec2( SMAA_THRESHOLD, SMAA_THRESHOLD );",

      // Calculate color deltas:
      "vec4 delta;",
      "vec3 C = texture2D( colorTex, texcoord ).rgb;",

      "vec3 Cleft = texture2D( colorTex, offset[0].xy ).rgb;",
      "vec3 t = abs( C - Cleft );",
      "delta.x = max( max( t.r, t.g ), t.b );",

      "vec3 Ctop = texture2D( colorTex, offset[0].zw ).rgb;",
      "t = abs( C - Ctop );",
      "delta.y = max( max( t.r, t.g ), t.b );",

      // We do the usual threshold:
      "vec2 edges = step( threshold, delta.xy );",

      // Then discard if there is no edge:
      "if ( dot( edges, vec2( 1.0, 1.0 ) ) == 0.0 )",
      "discard;",

      // Calculate right and bottom deltas:
      "vec3 Cright = texture2D( colorTex, offset[1].xy ).rgb;",
      "t = abs( C - Cright );",
      "delta.z = max( max( t.r, t.g ), t.b );",

      "vec3 Cbottom  = texture2D( colorTex, offset[1].zw ).rgb;",
      "t = abs( C - Cbottom );",
      "delta.w = max( max( t.r, t.g ), t.b );",

      // Calculate the maximum delta in the direct neighborhood:
      "float maxDelta = max( max( max( delta.x, delta.y ), delta.z ), delta.w );",

      // Calculate left-left and top-top deltas:
      "vec3 Cleftleft  = texture2D( colorTex, offset[2].xy ).rgb;",
      "t = abs( C - Cleftleft );",
      "delta.z = max( max( t.r, t.g ), t.b );",

      "vec3 Ctoptop = texture2D( colorTex, offset[2].zw ).rgb;",
      "t = abs( C - Ctoptop );",
      "delta.w = max( max( t.r, t.g ), t.b );",

      // Calculate the final maximum delta:
      "maxDelta = max( max( maxDelta, delta.z ), delta.w );",

      // Local contrast adaptation in action:
      "edges.xy *= step( 0.5 * maxDelta, delta.xy );",

      "return vec4( edges, 0.0, 0.0 );",
      "}",

      "void main() {",

      "gl_FragColor = SMAAColorEdgeDetectionPS( vUv, vOffset, tDiffuse );",

      "}"

    ].join("\n")

  };

  /** The Enhanced Subpixel Morphological Antialiasing Weights shader. */
  public static SMAAWeightsShader: any = {

    defines: {

      SMAA_MAX_SEARCH_STEPS: "8",
      SMAA_AREATEX_MAX_DISTANCE: "16",
      SMAA_AREATEX_PIXEL_SIZE: "( 1.0 / vec2( 160.0, 560.0 ) )",
      SMAA_AREATEX_SUBTEX_SIZE: "( 1.0 / 7.0 )"

    },

    uniforms: {

      tDiffuse: { value: null },
      tArea: { value: null },
      tSearch: { value: null },
      resolution: { value: new Vector2(1 / 1024, 1 / 512) }

    },

    vertexShader: [

      "uniform vec2 resolution;",

      "varying vec2 vUv;",
      "varying vec4 vOffset[ 3 ];",
      "varying vec2 vPixcoord;",

      "void SMAABlendingWeightCalculationVS( vec2 texcoord ) {",
      "vPixcoord = texcoord / resolution;",

      // We will use these offsets for the searches later on (see @PSEUDO_GATHER4):
      "vOffset[ 0 ] = texcoord.xyxy + resolution.xyxy * vec4( -0.25, 0.125, 1.25, 0.125 );", // WebGL port note: Changed sign in Y and W components
      "vOffset[ 1 ] = texcoord.xyxy + resolution.xyxy * vec4( -0.125, 0.25, -0.125, -1.25 );", // WebGL port note: Changed sign in Y and W components

      // And these for the searches, they indicate the ends of the loops:
      "vOffset[ 2 ] = vec4( vOffset[ 0 ].xz, vOffset[ 1 ].yw ) + vec4( -2.0, 2.0, -2.0, 2.0 ) * resolution.xxyy * float( SMAA_MAX_SEARCH_STEPS );",

      "}",

      "void main() {",

      "vUv = uv;",

      "SMAABlendingWeightCalculationVS( vUv );",

      "gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );",

      "}"

    ].join("\n"),

    fragmentShader: [

      "#define SMAASampleLevelZeroOffset( tex, coord, offset ) texture2D( tex, coord + float( offset ) * resolution, 0.0 )",

      "uniform sampler2D tDiffuse;",
      "uniform sampler2D tArea;",
      "uniform sampler2D tSearch;",
      "uniform vec2 resolution;",

      "varying vec2 vUv;",
      "varying vec4 vOffset[3];",
      "varying vec2 vPixcoord;",

      "vec2 round( vec2 x ) {",
      "return sign( x ) * floor( abs( x ) + 0.5 );",
      "}",

      "float SMAASearchLength( sampler2D searchTex, vec2 e, float bias, float scale ) {",
      // Not required if searchTex accesses are set to point:
      // float2 SEARCH_TEX_PIXEL_SIZE = 1.0 / float2(66.0, 33.0);
      // e = float2(bias, 0.0) + 0.5 * SEARCH_TEX_PIXEL_SIZE +
      //     e * float2(scale, 1.0) * float2(64.0, 32.0) * SEARCH_TEX_PIXEL_SIZE;
      "e.r = bias + e.r * scale;",
      "return 255.0 * texture2D( searchTex, e, 0.0 ).r;",
      "}",

      "float SMAASearchXLeft( sampler2D edgesTex, sampler2D searchTex, vec2 texcoord, float end ) {",
      /*
      * @PSEUDO_GATHER4
      * This texcoord has been offset by (-0.25, -0.125) in the vertex shader to
      * sample between edge, thus fetching four edges in a row.
      * Sampling with different offsets in each direction allows to disambiguate
      * which edges are active from the four fetched ones.
      */
      "vec2 e = vec2( 0.0, 1.0 );",

      "for ( int i = 0; i < SMAA_MAX_SEARCH_STEPS; i ++ ) {", // WebGL port note: Changed while to for
      "e = texture2D( edgesTex, texcoord, 0.0 ).rg;",
      "texcoord -= vec2( 2.0, 0.0 ) * resolution;",
      "if ( ! ( texcoord.x > end && e.g > 0.8281 && e.r == 0.0 ) ) break;",
      "}",

      // We correct the previous (-0.25, -0.125) offset we applied:
      "texcoord.x += 0.25 * resolution.x;",

      // The searches are bias by 1, so adjust the coords accordingly:
      "texcoord.x += resolution.x;",

      // Disambiguate the length added by the last step:
      "texcoord.x += 2.0 * resolution.x;", // Undo last step
      "texcoord.x -= resolution.x * SMAASearchLength(searchTex, e, 0.0, 0.5);",

      "return texcoord.x;",
      "}",

      "float SMAASearchXRight( sampler2D edgesTex, sampler2D searchTex, vec2 texcoord, float end ) {",
      "vec2 e = vec2( 0.0, 1.0 );",

      "for ( int i = 0; i < SMAA_MAX_SEARCH_STEPS; i ++ ) {", // WebGL port note: Changed while to for
      "e = texture2D( edgesTex, texcoord, 0.0 ).rg;",
      "texcoord += vec2( 2.0, 0.0 ) * resolution;",
      "if ( ! ( texcoord.x < end && e.g > 0.8281 && e.r == 0.0 ) ) break;",
      "}",

      "texcoord.x -= 0.25 * resolution.x;",
      "texcoord.x -= resolution.x;",
      "texcoord.x -= 2.0 * resolution.x;",
      "texcoord.x += resolution.x * SMAASearchLength( searchTex, e, 0.5, 0.5 );",

      "return texcoord.x;",
      "}",

      "float SMAASearchYUp( sampler2D edgesTex, sampler2D searchTex, vec2 texcoord, float end ) {",
      "vec2 e = vec2( 1.0, 0.0 );",

      "for ( int i = 0; i < SMAA_MAX_SEARCH_STEPS; i ++ ) {", // WebGL port note: Changed while to for
      "e = texture2D( edgesTex, texcoord, 0.0 ).rg;",
      "texcoord += vec2( 0.0, 2.0 ) * resolution;", // WebGL port note: Changed sign
      "if ( ! ( texcoord.y > end && e.r > 0.8281 && e.g == 0.0 ) ) break;",
      "}",

      "texcoord.y -= 0.25 * resolution.y;", // WebGL port note: Changed sign
      "texcoord.y -= resolution.y;", // WebGL port note: Changed sign
      "texcoord.y -= 2.0 * resolution.y;", // WebGL port note: Changed sign
      "texcoord.y += resolution.y * SMAASearchLength( searchTex, e.gr, 0.0, 0.5 );", // WebGL port note: Changed sign

      "return texcoord.y;",
      "}",

      "float SMAASearchYDown( sampler2D edgesTex, sampler2D searchTex, vec2 texcoord, float end ) {",
      "vec2 e = vec2( 1.0, 0.0 );",

      "for ( int i = 0; i < SMAA_MAX_SEARCH_STEPS; i ++ ) {", // WebGL port note: Changed while to for
      "e = texture2D( edgesTex, texcoord, 0.0 ).rg;",
      "texcoord -= vec2( 0.0, 2.0 ) * resolution;", // WebGL port note: Changed sign
      "if ( ! ( texcoord.y < end && e.r > 0.8281 && e.g == 0.0 ) ) break;",
      "}",

      "texcoord.y += 0.25 * resolution.y;", // WebGL port note: Changed sign
      "texcoord.y += resolution.y;", // WebGL port note: Changed sign
      "texcoord.y += 2.0 * resolution.y;", // WebGL port note: Changed sign
      "texcoord.y -= resolution.y * SMAASearchLength( searchTex, e.gr, 0.5, 0.5 );", // WebGL port note: Changed sign

      "return texcoord.y;",
      "}",

      "vec2 SMAAArea( sampler2D areaTex, vec2 dist, float e1, float e2, float offset ) {",
      // Rounding prevents precision errors of bilinear filtering:
      "vec2 texcoord = float( SMAA_AREATEX_MAX_DISTANCE ) * round( 4.0 * vec2( e1, e2 ) ) + dist;",

      // We do a scale and bias for mapping to texel space:
      "texcoord = SMAA_AREATEX_PIXEL_SIZE * texcoord + ( 0.5 * SMAA_AREATEX_PIXEL_SIZE );",

      // Move to proper place, according to the subpixel offset:
      "texcoord.y += SMAA_AREATEX_SUBTEX_SIZE * offset;",

      "return texture2D( areaTex, texcoord, 0.0 ).rg;",
      "}",

      "vec4 SMAABlendingWeightCalculationPS( vec2 texcoord, vec2 pixcoord, vec4 offset[ 3 ], sampler2D edgesTex, sampler2D areaTex, sampler2D searchTex, ivec4 subsampleIndices ) {",
      "vec4 weights = vec4( 0.0, 0.0, 0.0, 0.0 );",

      "vec2 e = texture2D( edgesTex, texcoord ).rg;",

      "if ( e.g > 0.0 ) {", // Edge at north
      "vec2 d;",

      // Find the distance to the left:
      "vec2 coords;",
      "coords.x = SMAASearchXLeft( edgesTex, searchTex, offset[ 0 ].xy, offset[ 2 ].x );",
      "coords.y = offset[ 1 ].y;", // offset[1].y = texcoord.y - 0.25 * resolution.y (@CROSSING_OFFSET)
      "d.x = coords.x;",

      // Now fetch the left crossing edges, two at a time using bilinear
      // filtering. Sampling at -0.25 (see @CROSSING_OFFSET) enables to
      // discern what value each edge has:
      "float e1 = texture2D( edgesTex, coords, 0.0 ).r;",

      // Find the distance to the right:
      "coords.x = SMAASearchXRight( edgesTex, searchTex, offset[ 0 ].zw, offset[ 2 ].y );",
      "d.y = coords.x;",

      // We want the distances to be in pixel units (doing this here allow to
      // better interleave arithmetic and memory accesses):
      "d = d / resolution.x - pixcoord.x;",

      // SMAAArea below needs a sqrt, as the areas texture is compressed
      // quadratically:
      "vec2 sqrt_d = sqrt( abs( d ) );",

      // Fetch the right crossing edges:
      "coords.y -= 1.0 * resolution.y;", // WebGL port note: Added
      "float e2 = SMAASampleLevelZeroOffset( edgesTex, coords, ivec2( 1, 0 ) ).r;",

      // Ok, we know how this pattern looks like, now it is time for getting
      // the actual area:
      "weights.rg = SMAAArea( areaTex, sqrt_d, e1, e2, float( subsampleIndices.y ) );",
      "}",

      "if ( e.r > 0.0 ) {", // Edge at west
      "vec2 d;",

      // Find the distance to the top:
      "vec2 coords;",

      "coords.y = SMAASearchYUp( edgesTex, searchTex, offset[ 1 ].xy, offset[ 2 ].z );",
      "coords.x = offset[ 0 ].x;", // offset[1].x = texcoord.x - 0.25 * resolution.x;
      "d.x = coords.y;",

      // Fetch the top crossing edges:
      "float e1 = texture2D( edgesTex, coords, 0.0 ).g;",

      // Find the distance to the bottom:
      "coords.y = SMAASearchYDown( edgesTex, searchTex, offset[ 1 ].zw, offset[ 2 ].w );",
      "d.y = coords.y;",

      // We want the distances to be in pixel units:
      "d = d / resolution.y - pixcoord.y;",

      // SMAAArea below needs a sqrt, as the areas texture is compressed
      // quadratically:
      "vec2 sqrt_d = sqrt( abs( d ) );",

      // Fetch the bottom crossing edges:
      "coords.y -= 1.0 * resolution.y;", // WebGL port note: Added
      "float e2 = SMAASampleLevelZeroOffset( edgesTex, coords, ivec2( 0, 1 ) ).g;",

      // Get the area for this direction:
      "weights.ba = SMAAArea( areaTex, sqrt_d, e1, e2, float( subsampleIndices.x ) );",
      "}",

      "return weights;",
      "}",

      "void main() {",

      "gl_FragColor = SMAABlendingWeightCalculationPS( vUv, vPixcoord, vOffset, tDiffuse, tArea, tSearch, ivec4( 0.0 ) );",

      "}"

    ].join("\n")

  };

  /** The Enhanced Subpixel Morphological Antialiasing Blend shader. */
  public static SMAABlendShader: any = {

    uniforms: {

      tDiffuse: { value: null },
      tColor: { value: null },
      resolution: { value: new Vector2(1 / 1024, 1 / 512) }

    },

    vertexShader: [

      "uniform vec2 resolution;",

      "varying vec2 vUv;",
      "varying vec4 vOffset[ 2 ];",

      "void SMAANeighborhoodBlendingVS( vec2 texcoord ) {",
      "vOffset[ 0 ] = texcoord.xyxy + resolution.xyxy * vec4( -1.0, 0.0, 0.0, 1.0 );", // WebGL port note: Changed sign in W component
      "vOffset[ 1 ] = texcoord.xyxy + resolution.xyxy * vec4( 1.0, 0.0, 0.0, -1.0 );", // WebGL port note: Changed sign in W component
      "}",

      "void main() {",

      "vUv = uv;",

      "SMAANeighborhoodBlendingVS( vUv );",

      "gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );",

      "}"

    ].join("\n"),

    fragmentShader: [

      "uniform sampler2D tDiffuse;",
      "uniform sampler2D tColor;",
      "uniform vec2 resolution;",

      "varying vec2 vUv;",
      "varying vec4 vOffset[ 2 ];",

      "vec4 SMAANeighborhoodBlendingPS( vec2 texcoord, vec4 offset[ 2 ], sampler2D colorTex, sampler2D blendTex ) {",
      // Fetch the blending weights for current pixel:
      "vec4 a;",
      "a.xz = texture2D( blendTex, texcoord ).xz;",
      "a.y = texture2D( blendTex, offset[ 1 ].zw ).g;",
      "a.w = texture2D( blendTex, offset[ 1 ].xy ).a;",

      // Is there any blending weight with a value greater than 0.0?
      "if ( dot(a, vec4( 1.0, 1.0, 1.0, 1.0 )) < 1e-5 ) {",
      "return texture2D( colorTex, texcoord, 0.0 );",
      "} else {",
      // Up to 4 lines can be crossing a pixel (one through each edge). We
      // favor blending by choosing the line with the maximum weight for each
      // direction:
      "vec2 offset;",
      "offset.x = a.a > a.b ? a.a : -a.b;", // left vs. right
      "offset.y = a.g > a.r ? -a.g : a.r;", // top vs. bottom // WebGL port note: Changed signs

      // Then we go in the direction that has the maximum weight:
      "if ( abs( offset.x ) > abs( offset.y )) {", // horizontal vs. vertical
      "offset.y = 0.0;",
      "} else {",
      "offset.x = 0.0;",
      "}",

      // Fetch the opposite color and lerp by hand:
      "vec4 C = texture2D( colorTex, texcoord, 0.0 );",
      "texcoord += sign( offset ) * resolution;",
      "vec4 Cop = texture2D( colorTex, texcoord, 0.0 );",
      "float s = abs( offset.x ) > abs( offset.y ) ? abs( offset.x ) : abs( offset.y );",

      // WebGL port note: Added gamma correction
      "C.xyz = pow(C.xyz, vec3(2.2));",
      "Cop.xyz = pow(Cop.xyz, vec3(2.2));",
      "vec4 mixed = mix(C, Cop, s);",
      "mixed.xyz = pow(mixed.xyz, vec3(1.0 / 2.2));",

      "return mixed;",
      "}",
      "}",

      "void main() {",

      "gl_FragColor = SMAANeighborhoodBlendingPS( vUv, vOffset, tColor, tDiffuse );",

      "}"

    ].join("\n")

  };

  /** The Fast approximate anti-aliasing Antialiasing shader. */
  public static FXAAShader: any = {

    uniforms: {

      tDiffuse: { value: null },
      resolution: { value: new Vector2(1 / 1024, 1 / 512) }

    },

    vertexShader: [

      "varying vec2 vUv;",

      "void main() {",

      "vUv = uv;",
      "gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );",

      "}"

    ].join("\n"),

    fragmentShader: [
      "precision highp float;",
      "",
      "uniform sampler2D tDiffuse;",
      "",
      "uniform vec2 resolution;",
      "",
      "varying vec2 vUv;",
      "",
      "// FXAA 3.11 implementation by NVIDIA, ported to WebGL by Agost Biro (biro@archilogic.com)",
      "",
      "//----------------------------------------------------------------------------------",
      "// File:        es3-kepler\FXAA\assets\shaders/FXAA_DefaultES.frag",
      "// SDK Version: v3.00",
      "// Email:       gameworks@nvidia.com",
      "// Site:        http://developer.nvidia.com/",
      "//",
      "// Copyright (c) 2014-2015, NVIDIA CORPORATION. All rights reserved.",
      "//",
      "// Redistribution and use in source and binary forms, with or without",
      "// modification, are permitted provided that the following conditions",
      "// are met:",
      "//  * Redistributions of source code must retain the above copyright",
      "//    notice, this list of conditions and the following disclaimer.",
      "//  * Redistributions in binary form must reproduce the above copyright",
      "//    notice, this list of conditions and the following disclaimer in the",
      "//    documentation and/or other materials provided with the distribution.",
      "//  * Neither the name of NVIDIA CORPORATION nor the names of its",
      "//    contributors may be used to endorse or promote products derived",
      "//    from this software without specific prior written permission.",
      "//",
      "// THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS ``AS IS'' AND ANY",
      "// EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE",
      "// IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR",
      "// PURPOSE ARE DISCLAIMED.  IN NO EVENT SHALL THE COPYRIGHT OWNER OR",
      "// CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL,",
      "// EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO,",
      "// PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR",
      "// PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY",
      "// OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT",
      "// (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE",
      "// OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.",
      "//",
      "//----------------------------------------------------------------------------------",
      "",
      "#define FXAA_PC 1",
      "#define FXAA_GLSL_100 1",
      "#define FXAA_QUALITY_PRESET 12",
      "",
      "#define FXAA_GREEN_AS_LUMA 1",
      "",
      "/*--------------------------------------------------------------------------*/",
      "#ifndef FXAA_PC_CONSOLE",
      "    //",
      "    // The console algorithm for PC is included",
      "    // for developers targeting really low spec machines.",
      "    // Likely better to just run FXAA_PC, and use a really low preset.",
      "    //",
      "    #define FXAA_PC_CONSOLE 0",
      "#endif",
      "/*--------------------------------------------------------------------------*/",
      "#ifndef FXAA_GLSL_120",
      "    #define FXAA_GLSL_120 0",
      "#endif",
      "/*--------------------------------------------------------------------------*/",
      "#ifndef FXAA_GLSL_130",
      "    #define FXAA_GLSL_130 0",
      "#endif",
      "/*--------------------------------------------------------------------------*/",
      "#ifndef FXAA_HLSL_3",
      "    #define FXAA_HLSL_3 0",
      "#endif",
      "/*--------------------------------------------------------------------------*/",
      "#ifndef FXAA_HLSL_4",
      "    #define FXAA_HLSL_4 0",
      "#endif",
      "/*--------------------------------------------------------------------------*/",
      "#ifndef FXAA_HLSL_5",
      "    #define FXAA_HLSL_5 0",
      "#endif",
      "/*==========================================================================*/",
      "#ifndef FXAA_GREEN_AS_LUMA",
      "    //",
      "    // For those using non-linear color,",
      "    // and either not able to get luma in alpha, or not wanting to,",
      "    // this enables FXAA to run using green as a proxy for luma.",
      "    // So with this enabled, no need to pack luma in alpha.",
      "    //",
      "    // This will turn off AA on anything which lacks some amount of green.",
      "    // Pure red and blue or combination of only R and B, will get no AA.",
      "    //",
      "    // Might want to lower the settings for both,",
      "    //    fxaaConsoleEdgeThresholdMin",
      "    //    fxaaQualityEdgeThresholdMin",
      "    // In order to insure AA does not get turned off on colors",
      "    // which contain a minor amount of green.",
      "    //",
      "    // 1 = On.",
      "    // 0 = Off.",
      "    //",
      "    #define FXAA_GREEN_AS_LUMA 0",
      "#endif",
      "/*--------------------------------------------------------------------------*/",
      "#ifndef FXAA_EARLY_EXIT",
      "    //",
      "    // Controls algorithm's early exit path.",
      "    // On PS3 turning this ON adds 2 cycles to the shader.",
      "    // On 360 turning this OFF adds 10ths of a millisecond to the shader.",
      "    // Turning this off on console will result in a more blurry image.",
      "    // So this defaults to on.",
      "    //",
      "    // 1 = On.",
      "    // 0 = Off.",
      "    //",
      "    #define FXAA_EARLY_EXIT 1",
      "#endif",
      "/*--------------------------------------------------------------------------*/",
      "#ifndef FXAA_DISCARD",
      "    //",
      "    // Only valid for PC OpenGL currently.",
      "    // Probably will not work when FXAA_GREEN_AS_LUMA = 1.",
      "    //",
      "    // 1 = Use discard on pixels which don't need AA.",
      "    //     For APIs which enable concurrent TEX+ROP from same surface.",
      "    // 0 = Return unchanged color on pixels which don't need AA.",
      "    //",
      "    #define FXAA_DISCARD 0",
      "#endif",
      "/*--------------------------------------------------------------------------*/",
      "#ifndef FXAA_FAST_PIXEL_OFFSET",
      "    //",
      "    // Used for GLSL 120 only.",
      "    //",
      "    // 1 = GL API supports fast pixel offsets",
      "    // 0 = do not use fast pixel offsets",
      "    //",
      "    #ifdef GL_EXT_gpu_shader4",
      "        #define FXAA_FAST_PIXEL_OFFSET 1",
      "    #endif",
      "    #ifdef GL_NV_gpu_shader5",
      "        #define FXAA_FAST_PIXEL_OFFSET 1",
      "    #endif",
      "    #ifdef GL_ARB_gpu_shader5",
      "        #define FXAA_FAST_PIXEL_OFFSET 1",
      "    #endif",
      "    #ifndef FXAA_FAST_PIXEL_OFFSET",
      "        #define FXAA_FAST_PIXEL_OFFSET 0",
      "    #endif",
      "#endif",
      "/*--------------------------------------------------------------------------*/",
      "#ifndef FXAA_GATHER4_ALPHA",
      "    //",
      "    // 1 = API supports gather4 on alpha channel.",
      "    // 0 = API does not support gather4 on alpha channel.",
      "    //",
      "    #if (FXAA_HLSL_5 == 1)",
      "        #define FXAA_GATHER4_ALPHA 1",
      "    #endif",
      "    #ifdef GL_ARB_gpu_shader5",
      "        #define FXAA_GATHER4_ALPHA 1",
      "    #endif",
      "    #ifdef GL_NV_gpu_shader5",
      "        #define FXAA_GATHER4_ALPHA 1",
      "    #endif",
      "    #ifndef FXAA_GATHER4_ALPHA",
      "        #define FXAA_GATHER4_ALPHA 0",
      "    #endif",
      "#endif",
      "",
      "",
      "/*============================================================================",
      "                        FXAA QUALITY - TUNING KNOBS",
      "------------------------------------------------------------------------------",
      "NOTE the other tuning knobs are now in the shader function inputs!",
      "============================================================================*/",
      "#ifndef FXAA_QUALITY_PRESET",
      "    //",
      "    // Choose the quality preset.",
      "    // This needs to be compiled into the shader as it effects code.",
      "    // Best option to include multiple presets is to",
      "    // in each shader define the preset, then include this file.",
      "    //",
      "    // OPTIONS",
      "    // -----------------------------------------------------------------------",
      "    // 10 to 15 - default medium dither (10=fastest, 15=highest quality)",
      "    // 20 to 29 - less dither, more expensive (20=fastest, 29=highest quality)",
      "    // 39       - no dither, very expensive",
      "    //",
      "    // NOTES",
      "    // -----------------------------------------------------------------------",
      "    // 12 = slightly faster then FXAA 3.9 and higher edge quality (default)",
      "    // 13 = about same speed as FXAA 3.9 and better than 12",
      "    // 23 = closest to FXAA 3.9 visually and performance wise",
      "    //  _ = the lowest digit is directly related to performance",
      "    // _  = the highest digit is directly related to style",
      "    //",
      "    #define FXAA_QUALITY_PRESET 12",
      "#endif",
      "",
      "",
      "/*============================================================================",
      "",
      "                           FXAA QUALITY - PRESETS",
      "",
      "============================================================================*/",
      "",
      "/*============================================================================",
      "                     FXAA QUALITY - MEDIUM DITHER PRESETS",
      "============================================================================*/",
      "#if (FXAA_QUALITY_PRESET == 10)",
      "    #define FXAA_QUALITY_PS 3",
      "    #define FXAA_QUALITY_P0 1.5",
      "    #define FXAA_QUALITY_P1 3.0",
      "    #define FXAA_QUALITY_P2 12.0",
      "#endif",
      "/*--------------------------------------------------------------------------*/",
      "#if (FXAA_QUALITY_PRESET == 11)",
      "    #define FXAA_QUALITY_PS 4",
      "    #define FXAA_QUALITY_P0 1.0",
      "    #define FXAA_QUALITY_P1 1.5",
      "    #define FXAA_QUALITY_P2 3.0",
      "    #define FXAA_QUALITY_P3 12.0",
      "#endif",
      "/*--------------------------------------------------------------------------*/",
      "#if (FXAA_QUALITY_PRESET == 12)",
      "    #define FXAA_QUALITY_PS 5",
      "    #define FXAA_QUALITY_P0 1.0",
      "    #define FXAA_QUALITY_P1 1.5",
      "    #define FXAA_QUALITY_P2 2.0",
      "    #define FXAA_QUALITY_P3 4.0",
      "    #define FXAA_QUALITY_P4 12.0",
      "#endif",
      "/*--------------------------------------------------------------------------*/",
      "#if (FXAA_QUALITY_PRESET == 13)",
      "    #define FXAA_QUALITY_PS 6",
      "    #define FXAA_QUALITY_P0 1.0",
      "    #define FXAA_QUALITY_P1 1.5",
      "    #define FXAA_QUALITY_P2 2.0",
      "    #define FXAA_QUALITY_P3 2.0",
      "    #define FXAA_QUALITY_P4 4.0",
      "    #define FXAA_QUALITY_P5 12.0",
      "#endif",
      "/*--------------------------------------------------------------------------*/",
      "#if (FXAA_QUALITY_PRESET == 14)",
      "    #define FXAA_QUALITY_PS 7",
      "    #define FXAA_QUALITY_P0 1.0",
      "    #define FXAA_QUALITY_P1 1.5",
      "    #define FXAA_QUALITY_P2 2.0",
      "    #define FXAA_QUALITY_P3 2.0",
      "    #define FXAA_QUALITY_P4 2.0",
      "    #define FXAA_QUALITY_P5 4.0",
      "    #define FXAA_QUALITY_P6 12.0",
      "#endif",
      "/*--------------------------------------------------------------------------*/",
      "#if (FXAA_QUALITY_PRESET == 15)",
      "    #define FXAA_QUALITY_PS 8",
      "    #define FXAA_QUALITY_P0 1.0",
      "    #define FXAA_QUALITY_P1 1.5",
      "    #define FXAA_QUALITY_P2 2.0",
      "    #define FXAA_QUALITY_P3 2.0",
      "    #define FXAA_QUALITY_P4 2.0",
      "    #define FXAA_QUALITY_P5 2.0",
      "    #define FXAA_QUALITY_P6 4.0",
      "    #define FXAA_QUALITY_P7 12.0",
      "#endif",
      "",
      "/*============================================================================",
      "                     FXAA QUALITY - LOW DITHER PRESETS",
      "============================================================================*/",
      "#if (FXAA_QUALITY_PRESET == 20)",
      "    #define FXAA_QUALITY_PS 3",
      "    #define FXAA_QUALITY_P0 1.5",
      "    #define FXAA_QUALITY_P1 2.0",
      "    #define FXAA_QUALITY_P2 8.0",
      "#endif",
      "/*--------------------------------------------------------------------------*/",
      "#if (FXAA_QUALITY_PRESET == 21)",
      "    #define FXAA_QUALITY_PS 4",
      "    #define FXAA_QUALITY_P0 1.0",
      "    #define FXAA_QUALITY_P1 1.5",
      "    #define FXAA_QUALITY_P2 2.0",
      "    #define FXAA_QUALITY_P3 8.0",
      "#endif",
      "/*--------------------------------------------------------------------------*/",
      "#if (FXAA_QUALITY_PRESET == 22)",
      "    #define FXAA_QUALITY_PS 5",
      "    #define FXAA_QUALITY_P0 1.0",
      "    #define FXAA_QUALITY_P1 1.5",
      "    #define FXAA_QUALITY_P2 2.0",
      "    #define FXAA_QUALITY_P3 2.0",
      "    #define FXAA_QUALITY_P4 8.0",
      "#endif",
      "/*--------------------------------------------------------------------------*/",
      "#if (FXAA_QUALITY_PRESET == 23)",
      "    #define FXAA_QUALITY_PS 6",
      "    #define FXAA_QUALITY_P0 1.0",
      "    #define FXAA_QUALITY_P1 1.5",
      "    #define FXAA_QUALITY_P2 2.0",
      "    #define FXAA_QUALITY_P3 2.0",
      "    #define FXAA_QUALITY_P4 2.0",
      "    #define FXAA_QUALITY_P5 8.0",
      "#endif",
      "/*--------------------------------------------------------------------------*/",
      "#if (FXAA_QUALITY_PRESET == 24)",
      "    #define FXAA_QUALITY_PS 7",
      "    #define FXAA_QUALITY_P0 1.0",
      "    #define FXAA_QUALITY_P1 1.5",
      "    #define FXAA_QUALITY_P2 2.0",
      "    #define FXAA_QUALITY_P3 2.0",
      "    #define FXAA_QUALITY_P4 2.0",
      "    #define FXAA_QUALITY_P5 3.0",
      "    #define FXAA_QUALITY_P6 8.0",
      "#endif",
      "/*--------------------------------------------------------------------------*/",
      "#if (FXAA_QUALITY_PRESET == 25)",
      "    #define FXAA_QUALITY_PS 8",
      "    #define FXAA_QUALITY_P0 1.0",
      "    #define FXAA_QUALITY_P1 1.5",
      "    #define FXAA_QUALITY_P2 2.0",
      "    #define FXAA_QUALITY_P3 2.0",
      "    #define FXAA_QUALITY_P4 2.0",
      "    #define FXAA_QUALITY_P5 2.0",
      "    #define FXAA_QUALITY_P6 4.0",
      "    #define FXAA_QUALITY_P7 8.0",
      "#endif",
      "/*--------------------------------------------------------------------------*/",
      "#if (FXAA_QUALITY_PRESET == 26)",
      "    #define FXAA_QUALITY_PS 9",
      "    #define FXAA_QUALITY_P0 1.0",
      "    #define FXAA_QUALITY_P1 1.5",
      "    #define FXAA_QUALITY_P2 2.0",
      "    #define FXAA_QUALITY_P3 2.0",
      "    #define FXAA_QUALITY_P4 2.0",
      "    #define FXAA_QUALITY_P5 2.0",
      "    #define FXAA_QUALITY_P6 2.0",
      "    #define FXAA_QUALITY_P7 4.0",
      "    #define FXAA_QUALITY_P8 8.0",
      "#endif",
      "/*--------------------------------------------------------------------------*/",
      "#if (FXAA_QUALITY_PRESET == 27)",
      "    #define FXAA_QUALITY_PS 10",
      "    #define FXAA_QUALITY_P0 1.0",
      "    #define FXAA_QUALITY_P1 1.5",
      "    #define FXAA_QUALITY_P2 2.0",
      "    #define FXAA_QUALITY_P3 2.0",
      "    #define FXAA_QUALITY_P4 2.0",
      "    #define FXAA_QUALITY_P5 2.0",
      "    #define FXAA_QUALITY_P6 2.0",
      "    #define FXAA_QUALITY_P7 2.0",
      "    #define FXAA_QUALITY_P8 4.0",
      "    #define FXAA_QUALITY_P9 8.0",
      "#endif",
      "/*--------------------------------------------------------------------------*/",
      "#if (FXAA_QUALITY_PRESET == 28)",
      "    #define FXAA_QUALITY_PS 11",
      "    #define FXAA_QUALITY_P0 1.0",
      "    #define FXAA_QUALITY_P1 1.5",
      "    #define FXAA_QUALITY_P2 2.0",
      "    #define FXAA_QUALITY_P3 2.0",
      "    #define FXAA_QUALITY_P4 2.0",
      "    #define FXAA_QUALITY_P5 2.0",
      "    #define FXAA_QUALITY_P6 2.0",
      "    #define FXAA_QUALITY_P7 2.0",
      "    #define FXAA_QUALITY_P8 2.0",
      "    #define FXAA_QUALITY_P9 4.0",
      "    #define FXAA_QUALITY_P10 8.0",
      "#endif",
      "/*--------------------------------------------------------------------------*/",
      "#if (FXAA_QUALITY_PRESET == 29)",
      "    #define FXAA_QUALITY_PS 12",
      "    #define FXAA_QUALITY_P0 1.0",
      "    #define FXAA_QUALITY_P1 1.5",
      "    #define FXAA_QUALITY_P2 2.0",
      "    #define FXAA_QUALITY_P3 2.0",
      "    #define FXAA_QUALITY_P4 2.0",
      "    #define FXAA_QUALITY_P5 2.0",
      "    #define FXAA_QUALITY_P6 2.0",
      "    #define FXAA_QUALITY_P7 2.0",
      "    #define FXAA_QUALITY_P8 2.0",
      "    #define FXAA_QUALITY_P9 2.0",
      "    #define FXAA_QUALITY_P10 4.0",
      "    #define FXAA_QUALITY_P11 8.0",
      "#endif",
      "",
      "/*============================================================================",
      "                     FXAA QUALITY - EXTREME QUALITY",
      "============================================================================*/",
      "#if (FXAA_QUALITY_PRESET == 39)",
      "    #define FXAA_QUALITY_PS 12",
      "    #define FXAA_QUALITY_P0 1.0",
      "    #define FXAA_QUALITY_P1 1.0",
      "    #define FXAA_QUALITY_P2 1.0",
      "    #define FXAA_QUALITY_P3 1.0",
      "    #define FXAA_QUALITY_P4 1.0",
      "    #define FXAA_QUALITY_P5 1.5",
      "    #define FXAA_QUALITY_P6 2.0",
      "    #define FXAA_QUALITY_P7 2.0",
      "    #define FXAA_QUALITY_P8 2.0",
      "    #define FXAA_QUALITY_P9 2.0",
      "    #define FXAA_QUALITY_P10 4.0",
      "    #define FXAA_QUALITY_P11 8.0",
      "#endif",
      "",
      "",
      "",
      "/*============================================================================",
      "",
      "                                API PORTING",
      "",
      "============================================================================*/",
      "#if (FXAA_GLSL_100 == 1) || (FXAA_GLSL_120 == 1) || (FXAA_GLSL_130 == 1)",
      "    #define FxaaBool bool",
      "    #define FxaaDiscard discard",
      "    #define FxaaFloat float",
      "    #define FxaaFloat2 vec2",
      "    #define FxaaFloat3 vec3",
      "    #define FxaaFloat4 vec4",
      "    #define FxaaHalf float",
      "    #define FxaaHalf2 vec2",
      "    #define FxaaHalf3 vec3",
      "    #define FxaaHalf4 vec4",
      "    #define FxaaInt2 ivec2",
      "    #define FxaaSat(x) clamp(x, 0.0, 1.0)",
      "    #define FxaaTex sampler2D",
      "#else",
      "    #define FxaaBool bool",
      "    #define FxaaDiscard clip(-1)",
      "    #define FxaaFloat float",
      "    #define FxaaFloat2 float2",
      "    #define FxaaFloat3 float3",
      "    #define FxaaFloat4 float4",
      "    #define FxaaHalf half",
      "    #define FxaaHalf2 half2",
      "    #define FxaaHalf3 half3",
      "    #define FxaaHalf4 half4",
      "    #define FxaaSat(x) saturate(x)",
      "#endif",
      "/*--------------------------------------------------------------------------*/",
      "#if (FXAA_GLSL_100 == 1)",
      "  #define FxaaTexTop(t, p) texture2D(t, p, 0.0)",
      "  #define FxaaTexOff(t, p, o, r) texture2D(t, p + (o * r), 0.0)",
      "#endif",
      "/*--------------------------------------------------------------------------*/",
      "#if (FXAA_GLSL_120 == 1)",
      "    // Requires,",
      "    //  #version 120",
      "    // And at least,",
      "    //  #extension GL_EXT_gpu_shader4 : enable",
      "    //  (or set FXAA_FAST_PIXEL_OFFSET 1 to work like DX9)",
      "    #define FxaaTexTop(t, p) texture2DLod(t, p, 0.0)",
      "    #if (FXAA_FAST_PIXEL_OFFSET == 1)",
      "        #define FxaaTexOff(t, p, o, r) texture2DLodOffset(t, p, 0.0, o)",
      "    #else",
      "        #define FxaaTexOff(t, p, o, r) texture2DLod(t, p + (o * r), 0.0)",
      "    #endif",
      "    #if (FXAA_GATHER4_ALPHA == 1)",
      "        // use #extension GL_ARB_gpu_shader5 : enable",
      "        #define FxaaTexAlpha4(t, p) textureGather(t, p, 3)",
      "        #define FxaaTexOffAlpha4(t, p, o) textureGatherOffset(t, p, o, 3)",
      "        #define FxaaTexGreen4(t, p) textureGather(t, p, 1)",
      "        #define FxaaTexOffGreen4(t, p, o) textureGatherOffset(t, p, o, 1)",
      "    #endif",
      "#endif",
      "/*--------------------------------------------------------------------------*/",
      "#if (FXAA_GLSL_130 == 1)",
      "    // Requires \"#version 130\" or better",
      "    #define FxaaTexTop(t, p) textureLod(t, p, 0.0)",
      "    #define FxaaTexOff(t, p, o, r) textureLodOffset(t, p, 0.0, o)",
      "    #if (FXAA_GATHER4_ALPHA == 1)",
      "        // use #extension GL_ARB_gpu_shader5 : enable",
      "        #define FxaaTexAlpha4(t, p) textureGather(t, p, 3)",
      "        #define FxaaTexOffAlpha4(t, p, o) textureGatherOffset(t, p, o, 3)",
      "        #define FxaaTexGreen4(t, p) textureGather(t, p, 1)",
      "        #define FxaaTexOffGreen4(t, p, o) textureGatherOffset(t, p, o, 1)",
      "    #endif",
      "#endif",
      "/*--------------------------------------------------------------------------*/",
      "#if (FXAA_HLSL_3 == 1)",
      "    #define FxaaInt2 float2",
      "    #define FxaaTex sampler2D",
      "    #define FxaaTexTop(t, p) tex2Dlod(t, float4(p, 0.0, 0.0))",
      "    #define FxaaTexOff(t, p, o, r) tex2Dlod(t, float4(p + (o * r), 0, 0))",
      "#endif",
      "/*--------------------------------------------------------------------------*/",
      "#if (FXAA_HLSL_4 == 1)",
      "    #define FxaaInt2 int2",
      "    struct FxaaTex { SamplerState smpl; Texture2D tex; };",
      "    #define FxaaTexTop(t, p) t.tex.SampleLevel(t.smpl, p, 0.0)",
      "    #define FxaaTexOff(t, p, o, r) t.tex.SampleLevel(t.smpl, p, 0.0, o)",
      "#endif",
      "/*--------------------------------------------------------------------------*/",
      "#if (FXAA_HLSL_5 == 1)",
      "    #define FxaaInt2 int2",
      "    struct FxaaTex { SamplerState smpl; Texture2D tex; };",
      "    #define FxaaTexTop(t, p) t.tex.SampleLevel(t.smpl, p, 0.0)",
      "    #define FxaaTexOff(t, p, o, r) t.tex.SampleLevel(t.smpl, p, 0.0, o)",
      "    #define FxaaTexAlpha4(t, p) t.tex.GatherAlpha(t.smpl, p)",
      "    #define FxaaTexOffAlpha4(t, p, o) t.tex.GatherAlpha(t.smpl, p, o)",
      "    #define FxaaTexGreen4(t, p) t.tex.GatherGreen(t.smpl, p)",
      "    #define FxaaTexOffGreen4(t, p, o) t.tex.GatherGreen(t.smpl, p, o)",
      "#endif",
      "",
      "",
      "/*============================================================================",
      "                   GREEN AS LUMA OPTION SUPPORT FUNCTION",
      "============================================================================*/",
      "#if (FXAA_GREEN_AS_LUMA == 0)",
      "    FxaaFloat FxaaLuma(FxaaFloat4 rgba) { return rgba.w; }",
      "#else",
      "    FxaaFloat FxaaLuma(FxaaFloat4 rgba) { return rgba.y; }",
      "#endif",
      "",
      "",
      "",
      "",
      "/*============================================================================",
      "",
      "                             FXAA3 QUALITY - PC",
      "",
      "============================================================================*/",
      "#if (FXAA_PC == 1)",
      "/*--------------------------------------------------------------------------*/",
      "FxaaFloat4 FxaaPixelShader(",
      "    //",
      "    // Use noperspective interpolation here (turn off perspective interpolation).",
      "    // {xy} = center of pixel",
      "    FxaaFloat2 pos,",
      "    //",
      "    // Used only for FXAA Console, and not used on the 360 version.",
      "    // Use noperspective interpolation here (turn off perspective interpolation).",
      "    // {xy_} = upper left of pixel",
      "    // {_zw} = lower right of pixel",
      "    FxaaFloat4 fxaaConsolePosPos,",
      "    //",
      "    // Input color texture.",
      "    // {rgb_} = color in linear or perceptual color space",
      "    // if (FXAA_GREEN_AS_LUMA == 0)",
      "    //     {__a} = luma in perceptual color space (not linear)",
      "    FxaaTex tex,",
      "    //",
      "    // Only used on the optimized 360 version of FXAA Console.",
      "    // For everything but 360, just use the same input here as for \"tex\".",
      "    // For 360, same texture, just alias with a 2nd sampler.",
      "    // This sampler needs to have an exponent bias of -1.",
      "    FxaaTex fxaaConsole360TexExpBiasNegOne,",
      "    //",
      "    // Only used on the optimized 360 version of FXAA Console.",
      "    // For everything but 360, just use the same input here as for \"tex\".",
      "    // For 360, same texture, just alias with a 3nd sampler.",
      "    // This sampler needs to have an exponent bias of -2.",
      "    FxaaTex fxaaConsole360TexExpBiasNegTwo,",
      "    //",
      "    // Only used on FXAA Quality.",
      "    // This must be from a constant/uniform.",
      "    // {x_} = 1.0/screenWidthInPixels",
      "    // {_y} = 1.0/screenHeightInPixels",
      "    FxaaFloat2 fxaaQualityRcpFrame,",
      "    //",
      "    // Only used on FXAA Console.",
      "    // This must be from a constant/uniform.",
      "    // This effects sub-pixel AA quality and inversely sharpness.",
      "    //   Where N ranges between,",
      "    //     N = 0.50 (default)",
      "    //     N = 0.33 (sharper)",
      "    // {x__} = -N/screenWidthInPixels",
      "    // {_y_} = -N/screenHeightInPixels",
      "    // {_z_} =  N/screenWidthInPixels",
      "    // {__w} =  N/screenHeightInPixels",
      "    FxaaFloat4 fxaaConsoleRcpFrameOpt,",
      "    //",
      "    // Only used on FXAA Console.",
      "    // Not used on 360, but used on PS3 and PC.",
      "    // This must be from a constant/uniform.",
      "    // {x__} = -2.0/screenWidthInPixels",
      "    // {_y_} = -2.0/screenHeightInPixels",
      "    // {_z_} =  2.0/screenWidthInPixels",
      "    // {__w} =  2.0/screenHeightInPixels",
      "    FxaaFloat4 fxaaConsoleRcpFrameOpt2,",
      "    //",
      "    // Only used on FXAA Console.",
      "    // Only used on 360 in place of fxaaConsoleRcpFrameOpt2.",
      "    // This must be from a constant/uniform.",
      "    // {x__} =  8.0/screenWidthInPixels",
      "    // {_y_} =  8.0/screenHeightInPixels",
      "    // {_z_} = -4.0/screenWidthInPixels",
      "    // {__w} = -4.0/screenHeightInPixels",
      "    FxaaFloat4 fxaaConsole360RcpFrameOpt2,",
      "    //",
      "    // Only used on FXAA Quality.",
      "    // This used to be the FXAA_QUALITY_SUBPIX define.",
      "    // It is here now to allow easier tuning.",
      "    // Choose the amount of sub-pixel aliasing removal.",
      "    // This can effect sharpness.",
      "    //   1.00 - upper limit (softer)",
      "    //   0.75 - default amount of filtering",
      "    //   0.50 - lower limit (sharper, less sub-pixel aliasing removal)",
      "    //   0.25 - almost off",
      "    //   0.00 - completely off",
      "    FxaaFloat fxaaQualitySubpix,",
      "    //",
      "    // Only used on FXAA Quality.",
      "    // This used to be the FXAA_QUALITY_EDGE_THRESHOLD define.",
      "    // It is here now to allow easier tuning.",
      "    // The minimum amount of local contrast required to apply algorithm.",
      "    //   0.333 - too little (faster)",
      "    //   0.250 - low quality",
      "    //   0.166 - default",
      "    //   0.125 - high quality",
      "    //   0.063 - overkill (slower)",
      "    FxaaFloat fxaaQualityEdgeThreshold,",
      "    //",
      "    // Only used on FXAA Quality.",
      "    // This used to be the FXAA_QUALITY_EDGE_THRESHOLD_MIN define.",
      "    // It is here now to allow easier tuning.",
      "    // Trims the algorithm from processing darks.",
      "    //   0.0833 - upper limit (default, the start of visible unfiltered edges)",
      "    //   0.0625 - high quality (faster)",
      "    //   0.0312 - visible limit (slower)",
      "    // Special notes when using FXAA_GREEN_AS_LUMA,",
      "    //   Likely want to set this to zero.",
      "    //   As colors that are mostly not-green",
      "    //   will appear very dark in the green channel!",
      "    //   Tune by looking at mostly non-green content,",
      "    //   then start at zero and increase until aliasing is a problem.",
      "    FxaaFloat fxaaQualityEdgeThresholdMin,",
      "    //",
      "    // Only used on FXAA Console.",
      "    // This used to be the FXAA_CONSOLE_EDGE_SHARPNESS define.",
      "    // It is here now to allow easier tuning.",
      "    // This does not effect PS3, as this needs to be compiled in.",
      "    //   Use FXAA_CONSOLE_PS3_EDGE_SHARPNESS for PS3.",
      "    //   Due to the PS3 being ALU bound,",
      "    //   there are only three safe values here: 2 and 4 and 8.",
      "    //   These options use the shaders ability to a free *|/ by 2|4|8.",
      "    // For all other platforms can be a non-power of two.",
      "    //   8.0 is sharper (default!!!)",
      "    //   4.0 is softer",
      "    //   2.0 is really soft (good only for vector graphics inputs)",
      "    FxaaFloat fxaaConsoleEdgeSharpness,",
      "    //",
      "    // Only used on FXAA Console.",
      "    // This used to be the FXAA_CONSOLE_EDGE_THRESHOLD define.",
      "    // It is here now to allow easier tuning.",
      "    // This does not effect PS3, as this needs to be compiled in.",
      "    //   Use FXAA_CONSOLE_PS3_EDGE_THRESHOLD for PS3.",
      "    //   Due to the PS3 being ALU bound,",
      "    //   there are only two safe values here: 1/4 and 1/8.",
      "    //   These options use the shaders ability to a free *|/ by 2|4|8.",
      "    // The console setting has a different mapping than the quality setting.",
      "    // Other platforms can use other values.",
      "    //   0.125 leaves less aliasing, but is softer (default!!!)",
      "    //   0.25 leaves more aliasing, and is sharper",
      "    FxaaFloat fxaaConsoleEdgeThreshold,",
      "    //",
      "    // Only used on FXAA Console.",
      "    // This used to be the FXAA_CONSOLE_EDGE_THRESHOLD_MIN define.",
      "    // It is here now to allow easier tuning.",
      "    // Trims the algorithm from processing darks.",
      "    // The console setting has a different mapping than the quality setting.",
      "    // This only applies when FXAA_EARLY_EXIT is 1.",
      "    // This does not apply to PS3,",
      "    // PS3 was simplified to avoid more shader instructions.",
      "    //   0.06 - faster but more aliasing in darks",
      "    //   0.05 - default",
      "    //   0.04 - slower and less aliasing in darks",
      "    // Special notes when using FXAA_GREEN_AS_LUMA,",
      "    //   Likely want to set this to zero.",
      "    //   As colors that are mostly not-green",
      "    //   will appear very dark in the green channel!",
      "    //   Tune by looking at mostly non-green content,",
      "    //   then start at zero and increase until aliasing is a problem.",
      "    FxaaFloat fxaaConsoleEdgeThresholdMin,",
      "    //",
      "    // Extra constants for 360 FXAA Console only.",
      "    // Use zeros or anything else for other platforms.",
      "    // These must be in physical constant registers and NOT immediates.",
      "    // Immediates will result in compiler un-optimizing.",
      "    // {xyzw} = float4(1.0, -1.0, 0.25, -0.25)",
      "    FxaaFloat4 fxaaConsole360ConstDir",
      ") {",
      "/*--------------------------------------------------------------------------*/",
      "    FxaaFloat2 posM;",
      "    posM.x = pos.x;",
      "    posM.y = pos.y;",
      "    #if (FXAA_GATHER4_ALPHA == 1)",
      "        #if (FXAA_DISCARD == 0)",
      "            FxaaFloat4 rgbyM = FxaaTexTop(tex, posM);",
      "            #if (FXAA_GREEN_AS_LUMA == 0)",
      "                #define lumaM rgbyM.w",
      "            #else",
      "                #define lumaM rgbyM.y",
      "            #endif",
      "        #endif",
      "        #if (FXAA_GREEN_AS_LUMA == 0)",
      "            FxaaFloat4 luma4A = FxaaTexAlpha4(tex, posM);",
      "            FxaaFloat4 luma4B = FxaaTexOffAlpha4(tex, posM, FxaaInt2(-1, -1));",
      "        #else",
      "            FxaaFloat4 luma4A = FxaaTexGreen4(tex, posM);",
      "            FxaaFloat4 luma4B = FxaaTexOffGreen4(tex, posM, FxaaInt2(-1, -1));",
      "        #endif",
      "        #if (FXAA_DISCARD == 1)",
      "            #define lumaM luma4A.w",
      "        #endif",
      "        #define lumaE luma4A.z",
      "        #define lumaS luma4A.x",
      "        #define lumaSE luma4A.y",
      "        #define lumaNW luma4B.w",
      "        #define lumaN luma4B.z",
      "        #define lumaW luma4B.x",
      "    #else",
      "        FxaaFloat4 rgbyM = FxaaTexTop(tex, posM);",
      "        #if (FXAA_GREEN_AS_LUMA == 0)",
      "            #define lumaM rgbyM.w",
      "        #else",
      "            #define lumaM rgbyM.y",
      "        #endif",
      "        #if (FXAA_GLSL_100 == 1)",
      "          FxaaFloat lumaS = FxaaLuma(FxaaTexOff(tex, posM, FxaaFloat2( 0.0, 1.0), fxaaQualityRcpFrame.xy));",
      "          FxaaFloat lumaE = FxaaLuma(FxaaTexOff(tex, posM, FxaaFloat2( 1.0, 0.0), fxaaQualityRcpFrame.xy));",
      "          FxaaFloat lumaN = FxaaLuma(FxaaTexOff(tex, posM, FxaaFloat2( 0.0,-1.0), fxaaQualityRcpFrame.xy));",
      "          FxaaFloat lumaW = FxaaLuma(FxaaTexOff(tex, posM, FxaaFloat2(-1.0, 0.0), fxaaQualityRcpFrame.xy));",
      "        #else",
      "          FxaaFloat lumaS = FxaaLuma(FxaaTexOff(tex, posM, FxaaInt2( 0, 1), fxaaQualityRcpFrame.xy));",
      "          FxaaFloat lumaE = FxaaLuma(FxaaTexOff(tex, posM, FxaaInt2( 1, 0), fxaaQualityRcpFrame.xy));",
      "          FxaaFloat lumaN = FxaaLuma(FxaaTexOff(tex, posM, FxaaInt2( 0,-1), fxaaQualityRcpFrame.xy));",
      "          FxaaFloat lumaW = FxaaLuma(FxaaTexOff(tex, posM, FxaaInt2(-1, 0), fxaaQualityRcpFrame.xy));",
      "        #endif",
      "    #endif",
      "/*--------------------------------------------------------------------------*/",
      "    FxaaFloat maxSM = max(lumaS, lumaM);",
      "    FxaaFloat minSM = min(lumaS, lumaM);",
      "    FxaaFloat maxESM = max(lumaE, maxSM);",
      "    FxaaFloat minESM = min(lumaE, minSM);",
      "    FxaaFloat maxWN = max(lumaN, lumaW);",
      "    FxaaFloat minWN = min(lumaN, lumaW);",
      "    FxaaFloat rangeMax = max(maxWN, maxESM);",
      "    FxaaFloat rangeMin = min(minWN, minESM);",
      "    FxaaFloat rangeMaxScaled = rangeMax * fxaaQualityEdgeThreshold;",
      "    FxaaFloat range = rangeMax - rangeMin;",
      "    FxaaFloat rangeMaxClamped = max(fxaaQualityEdgeThresholdMin, rangeMaxScaled);",
      "    FxaaBool earlyExit = range < rangeMaxClamped;",
      "/*--------------------------------------------------------------------------*/",
      "    if(earlyExit)",
      "        #if (FXAA_DISCARD == 1)",
      "            FxaaDiscard;",
      "        #else",
      "            return rgbyM;",
      "        #endif",
      "/*--------------------------------------------------------------------------*/",
      "    #if (FXAA_GATHER4_ALPHA == 0)",
      "        #if (FXAA_GLSL_100 == 1)",
      "          FxaaFloat lumaNW = FxaaLuma(FxaaTexOff(tex, posM, FxaaFloat2(-1.0,-1.0), fxaaQualityRcpFrame.xy));",
      "          FxaaFloat lumaSE = FxaaLuma(FxaaTexOff(tex, posM, FxaaFloat2( 1.0, 1.0), fxaaQualityRcpFrame.xy));",
      "          FxaaFloat lumaNE = FxaaLuma(FxaaTexOff(tex, posM, FxaaFloat2( 1.0,-1.0), fxaaQualityRcpFrame.xy));",
      "          FxaaFloat lumaSW = FxaaLuma(FxaaTexOff(tex, posM, FxaaFloat2(-1.0, 1.0), fxaaQualityRcpFrame.xy));",
      "        #else",
      "          FxaaFloat lumaNW = FxaaLuma(FxaaTexOff(tex, posM, FxaaInt2(-1,-1), fxaaQualityRcpFrame.xy));",
      "          FxaaFloat lumaSE = FxaaLuma(FxaaTexOff(tex, posM, FxaaInt2( 1, 1), fxaaQualityRcpFrame.xy));",
      "          FxaaFloat lumaNE = FxaaLuma(FxaaTexOff(tex, posM, FxaaInt2( 1,-1), fxaaQualityRcpFrame.xy));",
      "          FxaaFloat lumaSW = FxaaLuma(FxaaTexOff(tex, posM, FxaaInt2(-1, 1), fxaaQualityRcpFrame.xy));",
      "        #endif",
      "    #else",
      "        FxaaFloat lumaNE = FxaaLuma(FxaaTexOff(tex, posM, FxaaInt2(1, -1), fxaaQualityRcpFrame.xy));",
      "        FxaaFloat lumaSW = FxaaLuma(FxaaTexOff(tex, posM, FxaaInt2(-1, 1), fxaaQualityRcpFrame.xy));",
      "    #endif",
      "/*--------------------------------------------------------------------------*/",
      "    FxaaFloat lumaNS = lumaN + lumaS;",
      "    FxaaFloat lumaWE = lumaW + lumaE;",
      "    FxaaFloat subpixRcpRange = 1.0/range;",
      "    FxaaFloat subpixNSWE = lumaNS + lumaWE;",
      "    FxaaFloat edgeHorz1 = (-2.0 * lumaM) + lumaNS;",
      "    FxaaFloat edgeVert1 = (-2.0 * lumaM) + lumaWE;",
      "/*--------------------------------------------------------------------------*/",
      "    FxaaFloat lumaNESE = lumaNE + lumaSE;",
      "    FxaaFloat lumaNWNE = lumaNW + lumaNE;",
      "    FxaaFloat edgeHorz2 = (-2.0 * lumaE) + lumaNESE;",
      "    FxaaFloat edgeVert2 = (-2.0 * lumaN) + lumaNWNE;",
      "/*--------------------------------------------------------------------------*/",
      "    FxaaFloat lumaNWSW = lumaNW + lumaSW;",
      "    FxaaFloat lumaSWSE = lumaSW + lumaSE;",
      "    FxaaFloat edgeHorz4 = (abs(edgeHorz1) * 2.0) + abs(edgeHorz2);",
      "    FxaaFloat edgeVert4 = (abs(edgeVert1) * 2.0) + abs(edgeVert2);",
      "    FxaaFloat edgeHorz3 = (-2.0 * lumaW) + lumaNWSW;",
      "    FxaaFloat edgeVert3 = (-2.0 * lumaS) + lumaSWSE;",
      "    FxaaFloat edgeHorz = abs(edgeHorz3) + edgeHorz4;",
      "    FxaaFloat edgeVert = abs(edgeVert3) + edgeVert4;",
      "/*--------------------------------------------------------------------------*/",
      "    FxaaFloat subpixNWSWNESE = lumaNWSW + lumaNESE;",
      "    FxaaFloat lengthSign = fxaaQualityRcpFrame.x;",
      "    FxaaBool horzSpan = edgeHorz >= edgeVert;",
      "    FxaaFloat subpixA = subpixNSWE * 2.0 + subpixNWSWNESE;",
      "/*--------------------------------------------------------------------------*/",
      "    if(!horzSpan) lumaN = lumaW;",
      "    if(!horzSpan) lumaS = lumaE;",
      "    if(horzSpan) lengthSign = fxaaQualityRcpFrame.y;",
      "    FxaaFloat subpixB = (subpixA * (1.0/12.0)) - lumaM;",
      "/*--------------------------------------------------------------------------*/",
      "    FxaaFloat gradientN = lumaN - lumaM;",
      "    FxaaFloat gradientS = lumaS - lumaM;",
      "    FxaaFloat lumaNN = lumaN + lumaM;",
      "    FxaaFloat lumaSS = lumaS + lumaM;",
      "    FxaaBool pairN = abs(gradientN) >= abs(gradientS);",
      "    FxaaFloat gradient = max(abs(gradientN), abs(gradientS));",
      "    if(pairN) lengthSign = -lengthSign;",
      "    FxaaFloat subpixC = FxaaSat(abs(subpixB) * subpixRcpRange);",
      "/*--------------------------------------------------------------------------*/",
      "    FxaaFloat2 posB;",
      "    posB.x = posM.x;",
      "    posB.y = posM.y;",
      "    FxaaFloat2 offNP;",
      "    offNP.x = (!horzSpan) ? 0.0 : fxaaQualityRcpFrame.x;",
      "    offNP.y = ( horzSpan) ? 0.0 : fxaaQualityRcpFrame.y;",
      "    if(!horzSpan) posB.x += lengthSign * 0.5;",
      "    if( horzSpan) posB.y += lengthSign * 0.5;",
      "/*--------------------------------------------------------------------------*/",
      "    FxaaFloat2 posN;",
      "    posN.x = posB.x - offNP.x * FXAA_QUALITY_P0;",
      "    posN.y = posB.y - offNP.y * FXAA_QUALITY_P0;",
      "    FxaaFloat2 posP;",
      "    posP.x = posB.x + offNP.x * FXAA_QUALITY_P0;",
      "    posP.y = posB.y + offNP.y * FXAA_QUALITY_P0;",
      "    FxaaFloat subpixD = ((-2.0)*subpixC) + 3.0;",
      "    FxaaFloat lumaEndN = FxaaLuma(FxaaTexTop(tex, posN));",
      "    FxaaFloat subpixE = subpixC * subpixC;",
      "    FxaaFloat lumaEndP = FxaaLuma(FxaaTexTop(tex, posP));",
      "/*--------------------------------------------------------------------------*/",
      "    if(!pairN) lumaNN = lumaSS;",
      "    FxaaFloat gradientScaled = gradient * 1.0/4.0;",
      "    FxaaFloat lumaMM = lumaM - lumaNN * 0.5;",
      "    FxaaFloat subpixF = subpixD * subpixE;",
      "    FxaaBool lumaMLTZero = lumaMM < 0.0;",
      "/*--------------------------------------------------------------------------*/",
      "    lumaEndN -= lumaNN * 0.5;",
      "    lumaEndP -= lumaNN * 0.5;",
      "    FxaaBool doneN = abs(lumaEndN) >= gradientScaled;",
      "    FxaaBool doneP = abs(lumaEndP) >= gradientScaled;",
      "    if(!doneN) posN.x -= offNP.x * FXAA_QUALITY_P1;",
      "    if(!doneN) posN.y -= offNP.y * FXAA_QUALITY_P1;",
      "    FxaaBool doneNP = (!doneN) || (!doneP);",
      "    if(!doneP) posP.x += offNP.x * FXAA_QUALITY_P1;",
      "    if(!doneP) posP.y += offNP.y * FXAA_QUALITY_P1;",
      "/*--------------------------------------------------------------------------*/",
      "    if(doneNP) {",
      "        if(!doneN) lumaEndN = FxaaLuma(FxaaTexTop(tex, posN.xy));",
      "        if(!doneP) lumaEndP = FxaaLuma(FxaaTexTop(tex, posP.xy));",
      "        if(!doneN) lumaEndN = lumaEndN - lumaNN * 0.5;",
      "        if(!doneP) lumaEndP = lumaEndP - lumaNN * 0.5;",
      "        doneN = abs(lumaEndN) >= gradientScaled;",
      "        doneP = abs(lumaEndP) >= gradientScaled;",
      "        if(!doneN) posN.x -= offNP.x * FXAA_QUALITY_P2;",
      "        if(!doneN) posN.y -= offNP.y * FXAA_QUALITY_P2;",
      "        doneNP = (!doneN) || (!doneP);",
      "        if(!doneP) posP.x += offNP.x * FXAA_QUALITY_P2;",
      "        if(!doneP) posP.y += offNP.y * FXAA_QUALITY_P2;",
      "/*--------------------------------------------------------------------------*/",
      "        #if (FXAA_QUALITY_PS > 3)",
      "        if(doneNP) {",
      "            if(!doneN) lumaEndN = FxaaLuma(FxaaTexTop(tex, posN.xy));",
      "            if(!doneP) lumaEndP = FxaaLuma(FxaaTexTop(tex, posP.xy));",
      "            if(!doneN) lumaEndN = lumaEndN - lumaNN * 0.5;",
      "            if(!doneP) lumaEndP = lumaEndP - lumaNN * 0.5;",
      "            doneN = abs(lumaEndN) >= gradientScaled;",
      "            doneP = abs(lumaEndP) >= gradientScaled;",
      "            if(!doneN) posN.x -= offNP.x * FXAA_QUALITY_P3;",
      "            if(!doneN) posN.y -= offNP.y * FXAA_QUALITY_P3;",
      "            doneNP = (!doneN) || (!doneP);",
      "            if(!doneP) posP.x += offNP.x * FXAA_QUALITY_P3;",
      "            if(!doneP) posP.y += offNP.y * FXAA_QUALITY_P3;",
      "/*--------------------------------------------------------------------------*/",
      "            #if (FXAA_QUALITY_PS > 4)",
      "            if(doneNP) {",
      "                if(!doneN) lumaEndN = FxaaLuma(FxaaTexTop(tex, posN.xy));",
      "                if(!doneP) lumaEndP = FxaaLuma(FxaaTexTop(tex, posP.xy));",
      "                if(!doneN) lumaEndN = lumaEndN - lumaNN * 0.5;",
      "                if(!doneP) lumaEndP = lumaEndP - lumaNN * 0.5;",
      "                doneN = abs(lumaEndN) >= gradientScaled;",
      "                doneP = abs(lumaEndP) >= gradientScaled;",
      "                if(!doneN) posN.x -= offNP.x * FXAA_QUALITY_P4;",
      "                if(!doneN) posN.y -= offNP.y * FXAA_QUALITY_P4;",
      "                doneNP = (!doneN) || (!doneP);",
      "                if(!doneP) posP.x += offNP.x * FXAA_QUALITY_P4;",
      "                if(!doneP) posP.y += offNP.y * FXAA_QUALITY_P4;",
      "/*--------------------------------------------------------------------------*/",
      "                #if (FXAA_QUALITY_PS > 5)",
      "                if(doneNP) {",
      "                    if(!doneN) lumaEndN = FxaaLuma(FxaaTexTop(tex, posN.xy));",
      "                    if(!doneP) lumaEndP = FxaaLuma(FxaaTexTop(tex, posP.xy));",
      "                    if(!doneN) lumaEndN = lumaEndN - lumaNN * 0.5;",
      "                    if(!doneP) lumaEndP = lumaEndP - lumaNN * 0.5;",
      "                    doneN = abs(lumaEndN) >= gradientScaled;",
      "                    doneP = abs(lumaEndP) >= gradientScaled;",
      "                    if(!doneN) posN.x -= offNP.x * FXAA_QUALITY_P5;",
      "                    if(!doneN) posN.y -= offNP.y * FXAA_QUALITY_P5;",
      "                    doneNP = (!doneN) || (!doneP);",
      "                    if(!doneP) posP.x += offNP.x * FXAA_QUALITY_P5;",
      "                    if(!doneP) posP.y += offNP.y * FXAA_QUALITY_P5;",
      "/*--------------------------------------------------------------------------*/",
      "                    #if (FXAA_QUALITY_PS > 6)",
      "                    if(doneNP) {",
      "                        if(!doneN) lumaEndN = FxaaLuma(FxaaTexTop(tex, posN.xy));",
      "                        if(!doneP) lumaEndP = FxaaLuma(FxaaTexTop(tex, posP.xy));",
      "                        if(!doneN) lumaEndN = lumaEndN - lumaNN * 0.5;",
      "                        if(!doneP) lumaEndP = lumaEndP - lumaNN * 0.5;",
      "                        doneN = abs(lumaEndN) >= gradientScaled;",
      "                        doneP = abs(lumaEndP) >= gradientScaled;",
      "                        if(!doneN) posN.x -= offNP.x * FXAA_QUALITY_P6;",
      "                        if(!doneN) posN.y -= offNP.y * FXAA_QUALITY_P6;",
      "                        doneNP = (!doneN) || (!doneP);",
      "                        if(!doneP) posP.x += offNP.x * FXAA_QUALITY_P6;",
      "                        if(!doneP) posP.y += offNP.y * FXAA_QUALITY_P6;",
      "/*--------------------------------------------------------------------------*/",
      "                        #if (FXAA_QUALITY_PS > 7)",
      "                        if(doneNP) {",
      "                            if(!doneN) lumaEndN = FxaaLuma(FxaaTexTop(tex, posN.xy));",
      "                            if(!doneP) lumaEndP = FxaaLuma(FxaaTexTop(tex, posP.xy));",
      "                            if(!doneN) lumaEndN = lumaEndN - lumaNN * 0.5;",
      "                            if(!doneP) lumaEndP = lumaEndP - lumaNN * 0.5;",
      "                            doneN = abs(lumaEndN) >= gradientScaled;",
      "                            doneP = abs(lumaEndP) >= gradientScaled;",
      "                            if(!doneN) posN.x -= offNP.x * FXAA_QUALITY_P7;",
      "                            if(!doneN) posN.y -= offNP.y * FXAA_QUALITY_P7;",
      "                            doneNP = (!doneN) || (!doneP);",
      "                            if(!doneP) posP.x += offNP.x * FXAA_QUALITY_P7;",
      "                            if(!doneP) posP.y += offNP.y * FXAA_QUALITY_P7;",
      "/*--------------------------------------------------------------------------*/",
      "    #if (FXAA_QUALITY_PS > 8)",
      "    if(doneNP) {",
      "        if(!doneN) lumaEndN = FxaaLuma(FxaaTexTop(tex, posN.xy));",
      "        if(!doneP) lumaEndP = FxaaLuma(FxaaTexTop(tex, posP.xy));",
      "        if(!doneN) lumaEndN = lumaEndN - lumaNN * 0.5;",
      "        if(!doneP) lumaEndP = lumaEndP - lumaNN * 0.5;",
      "        doneN = abs(lumaEndN) >= gradientScaled;",
      "        doneP = abs(lumaEndP) >= gradientScaled;",
      "        if(!doneN) posN.x -= offNP.x * FXAA_QUALITY_P8;",
      "        if(!doneN) posN.y -= offNP.y * FXAA_QUALITY_P8;",
      "        doneNP = (!doneN) || (!doneP);",
      "        if(!doneP) posP.x += offNP.x * FXAA_QUALITY_P8;",
      "        if(!doneP) posP.y += offNP.y * FXAA_QUALITY_P8;",
      "/*--------------------------------------------------------------------------*/",
      "        #if (FXAA_QUALITY_PS > 9)",
      "        if(doneNP) {",
      "            if(!doneN) lumaEndN = FxaaLuma(FxaaTexTop(tex, posN.xy));",
      "            if(!doneP) lumaEndP = FxaaLuma(FxaaTexTop(tex, posP.xy));",
      "            if(!doneN) lumaEndN = lumaEndN - lumaNN * 0.5;",
      "            if(!doneP) lumaEndP = lumaEndP - lumaNN * 0.5;",
      "            doneN = abs(lumaEndN) >= gradientScaled;",
      "            doneP = abs(lumaEndP) >= gradientScaled;",
      "            if(!doneN) posN.x -= offNP.x * FXAA_QUALITY_P9;",
      "            if(!doneN) posN.y -= offNP.y * FXAA_QUALITY_P9;",
      "            doneNP = (!doneN) || (!doneP);",
      "            if(!doneP) posP.x += offNP.x * FXAA_QUALITY_P9;",
      "            if(!doneP) posP.y += offNP.y * FXAA_QUALITY_P9;",
      "/*--------------------------------------------------------------------------*/",
      "            #if (FXAA_QUALITY_PS > 10)",
      "            if(doneNP) {",
      "                if(!doneN) lumaEndN = FxaaLuma(FxaaTexTop(tex, posN.xy));",
      "                if(!doneP) lumaEndP = FxaaLuma(FxaaTexTop(tex, posP.xy));",
      "                if(!doneN) lumaEndN = lumaEndN - lumaNN * 0.5;",
      "                if(!doneP) lumaEndP = lumaEndP - lumaNN * 0.5;",
      "                doneN = abs(lumaEndN) >= gradientScaled;",
      "                doneP = abs(lumaEndP) >= gradientScaled;",
      "                if(!doneN) posN.x -= offNP.x * FXAA_QUALITY_P10;",
      "                if(!doneN) posN.y -= offNP.y * FXAA_QUALITY_P10;",
      "                doneNP = (!doneN) || (!doneP);",
      "                if(!doneP) posP.x += offNP.x * FXAA_QUALITY_P10;",
      "                if(!doneP) posP.y += offNP.y * FXAA_QUALITY_P10;",
      "/*--------------------------------------------------------------------------*/",
      "                #if (FXAA_QUALITY_PS > 11)",
      "                if(doneNP) {",
      "                    if(!doneN) lumaEndN = FxaaLuma(FxaaTexTop(tex, posN.xy));",
      "                    if(!doneP) lumaEndP = FxaaLuma(FxaaTexTop(tex, posP.xy));",
      "                    if(!doneN) lumaEndN = lumaEndN - lumaNN * 0.5;",
      "                    if(!doneP) lumaEndP = lumaEndP - lumaNN * 0.5;",
      "                    doneN = abs(lumaEndN) >= gradientScaled;",
      "                    doneP = abs(lumaEndP) >= gradientScaled;",
      "                    if(!doneN) posN.x -= offNP.x * FXAA_QUALITY_P11;",
      "                    if(!doneN) posN.y -= offNP.y * FXAA_QUALITY_P11;",
      "                    doneNP = (!doneN) || (!doneP);",
      "                    if(!doneP) posP.x += offNP.x * FXAA_QUALITY_P11;",
      "                    if(!doneP) posP.y += offNP.y * FXAA_QUALITY_P11;",
      "/*--------------------------------------------------------------------------*/",
      "                    #if (FXAA_QUALITY_PS > 12)",
      "                    if(doneNP) {",
      "                        if(!doneN) lumaEndN = FxaaLuma(FxaaTexTop(tex, posN.xy));",
      "                        if(!doneP) lumaEndP = FxaaLuma(FxaaTexTop(tex, posP.xy));",
      "                        if(!doneN) lumaEndN = lumaEndN - lumaNN * 0.5;",
      "                        if(!doneP) lumaEndP = lumaEndP - lumaNN * 0.5;",
      "                        doneN = abs(lumaEndN) >= gradientScaled;",
      "                        doneP = abs(lumaEndP) >= gradientScaled;",
      "                        if(!doneN) posN.x -= offNP.x * FXAA_QUALITY_P12;",
      "                        if(!doneN) posN.y -= offNP.y * FXAA_QUALITY_P12;",
      "                        doneNP = (!doneN) || (!doneP);",
      "                        if(!doneP) posP.x += offNP.x * FXAA_QUALITY_P12;",
      "                        if(!doneP) posP.y += offNP.y * FXAA_QUALITY_P12;",
      "/*--------------------------------------------------------------------------*/",
      "                    }",
      "                    #endif",
      "/*--------------------------------------------------------------------------*/",
      "                }",
      "                #endif",
      "/*--------------------------------------------------------------------------*/",
      "            }",
      "            #endif",
      "/*--------------------------------------------------------------------------*/",
      "        }",
      "        #endif",
      "/*--------------------------------------------------------------------------*/",
      "    }",
      "    #endif",
      "/*--------------------------------------------------------------------------*/",
      "                        }",
      "                        #endif",
      "/*--------------------------------------------------------------------------*/",
      "                    }",
      "                    #endif",
      "/*--------------------------------------------------------------------------*/",
      "                }",
      "                #endif",
      "/*--------------------------------------------------------------------------*/",
      "            }",
      "            #endif",
      "/*--------------------------------------------------------------------------*/",
      "        }",
      "        #endif",
      "/*--------------------------------------------------------------------------*/",
      "    }",
      "/*--------------------------------------------------------------------------*/",
      "    FxaaFloat dstN = posM.x - posN.x;",
      "    FxaaFloat dstP = posP.x - posM.x;",
      "    if(!horzSpan) dstN = posM.y - posN.y;",
      "    if(!horzSpan) dstP = posP.y - posM.y;",
      "/*--------------------------------------------------------------------------*/",
      "    FxaaBool goodSpanN = (lumaEndN < 0.0) != lumaMLTZero;",
      "    FxaaFloat spanLength = (dstP + dstN);",
      "    FxaaBool goodSpanP = (lumaEndP < 0.0) != lumaMLTZero;",
      "    FxaaFloat spanLengthRcp = 1.0/spanLength;",
      "/*--------------------------------------------------------------------------*/",
      "    FxaaBool directionN = dstN < dstP;",
      "    FxaaFloat dst = min(dstN, dstP);",
      "    FxaaBool goodSpan = directionN ? goodSpanN : goodSpanP;",
      "    FxaaFloat subpixG = subpixF * subpixF;",
      "    FxaaFloat pixelOffset = (dst * (-spanLengthRcp)) + 0.5;",
      "    FxaaFloat subpixH = subpixG * fxaaQualitySubpix;",
      "/*--------------------------------------------------------------------------*/",
      "    FxaaFloat pixelOffsetGood = goodSpan ? pixelOffset : 0.0;",
      "    FxaaFloat pixelOffsetSubpix = max(pixelOffsetGood, subpixH);",
      "    if(!horzSpan) posM.x += pixelOffsetSubpix * lengthSign;",
      "    if( horzSpan) posM.y += pixelOffsetSubpix * lengthSign;",
      "    #if (FXAA_DISCARD == 1)",
      "        return FxaaTexTop(tex, posM);",
      "    #else",
      "        return FxaaFloat4(FxaaTexTop(tex, posM).xyz, lumaM);",
      "    #endif",
      "}",
      "/*==========================================================================*/",
      "#endif",
      "",
      "void main() {",
      "  gl_FragColor = FxaaPixelShader(",
      "    vUv,",
      "    vec4(0.0),",
      "    tDiffuse,",
      "    tDiffuse,",
      "    tDiffuse,",
      "    resolution,",
      "    vec4(0.0),",
      "    vec4(0.0),",
      "    vec4(0.0),",
      "    0.75,",
      "    0.166,",
      "    0.0833,",
      "    0.0,",
      "    0.0,",
      "    0.0,",
      "    vec4(0.0)",
      "  );",
      "",
      "  // TODO avoid querying texture twice for same texel",
      "  gl_FragColor.a = texture2D(tDiffuse, vUv).a;",
      "}"
    ].join("\n")

  };
}
